import { RENDERCARDTYPE } from "@/constants/renderCardType";
import useAppStore from "@/context/store";
import { getMasonryConf, masonryCompute } from "@/context/useWidthCompute";
import { getArtImageSearchContent, getRelatedBoards } from "@/services";
import { getExpsValue } from "@/tracker/abTest";
import { useResetOnce } from "@/utils/hooks/useOnce";
import useOnceInViewport from "@/utils/hooks/useOnceInViewport";
import useRequest from "@/utils/useReq";
import { useMemoizedFn, useSetState } from "ahooks";
import { debounce, last } from "lodash";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import BoardSwiper from "../BoardSwiper";
import styles from "../index.module.less";
import PinRecommendPinList from "../PinRecommendPinList";

interface RecommendNewProps {
  isModal: boolean;
  pin: IPin;
  relatedBoards: any[];
  similiarList: any;
  showSimilarList: boolean;
  searchWord?: string;
  stackIndex?: number; // 新增：弹窗堆栈索引，用于生成唯一key
}

function RecommendNew({
  isModal,
  pin,
  relatedBoards,
  similiarList,
  showSimilarList,
  searchWord,
  stackIndex = 0,
}: RecommendNewProps) {
  const { headerHeight } = useAppStore(({ headerHeight }) => ({
    headerHeight,
  }));

  // 推荐列表最小高度需要减去的部分
  const reduceHeight = useMemo(() => {
    let height = 0;
    if (!isModal) {
      height = headerHeight + 80; // 搜索栏高度
    } else {
      // 弹窗情况下
      height = 70;
    }
    return height;
  }, [headerHeight, isModal]);

  const containerRef = useRef(null);
  const inViewport = useOnceInViewport(containerRef);

  // 同在画板数据管理
  const pinId = pin?.pin_id;
  const [query, setQuery] = useSetState({
    max: relatedBoards?.length ? last(relatedBoards).seq : 0,
    pinId: null,
  });
  const [hasMore, setHasMore] = useState(true);
  const [boardDataSource, setBoardDataSource] = useState(relatedBoards || []);
  const [artRecommend, setArtRecommend] = useState(null);
  const ssrInitRef = useRef(!!relatedBoards);

  const [getArtSearchContentFn, resetGetArtSearchContentFn] = useResetOnce(
    getArtImageSearchContent
  );

  // 搜索词变化时，重置getArtSearchContentFn
  useEffect(() => {
    if (searchWord) {
      resetGetArtSearchContentFn();
    }
  }, [resetGetArtSearchContentFn, searchWord]);

  // 获取 artRecommend 数据（只在初始化时获取一次）
  useRequest(
    async () => {
      if (!pin?.pin_id || !searchWord) {
        return;
      }

      const artAB = await getExpsValue("art_recommend_board_list", "old");

      if (artAB === "new") {
        const artResult = await getArtSearchContentFn({
          page_num: 1,
          page_size: 10,
          position: "search_result_post",
          ugc_publish_type: 1,
          pri_classify: 1,
          file_id: pin.file_id,
        });

        if (artResult?.data?.length >= 8) {
          setArtRecommend({
            type: RENDERCARDTYPE.artRecommend,
            pinId: RENDERCARDTYPE.artRecommend,
            pin_id: RENDERCARDTYPE.artRecommend, // 添加 pin_id 字段避免被过滤
            data: {
              pins: artResult.data,
              file: pin.file,
              fileId: pin.file_id,
            },
          });
        }
      }
    },
    {
      refreshDeps: [pin?.pin_id],
    }
  );

  // 获取同在画板数据
  useRequest(
    async () => {
      const { max, pinId } = query;

      if (!pinId) {
        return;
      }

      // 如果服务端有初始化渲染，不做第一次请求，避免多余请求
      if (ssrInitRef.current) {
        ssrInitRef.current = false;
        return;
      }

      const result = await getRelatedBoards({
        pin_id: pinId,
        limit: 20,
        ...(max ? { max } : null),
      });

      const boards = result?.boards || [];

      if (boards.length <= 0) {
        setHasMore(false);
      }

      setBoardDataSource(boardDataSource.concat(boards));
    },
    {
      refreshDeps: [query],
    }
  );

  useEffect(() => {
    if (pinId) {
      setHasMore(true);
      setBoardDataSource(relatedBoards || []);
      setQuery({
        max: relatedBoards?.length ? last(relatedBoards).seq : 0,
        pinId,
      });
    }
  }, [pinId, relatedBoards, setQuery]);

  const [column, setColumn] = useState<{
    num: number;
    columnSize: number;
    wrapperWidth: number;
  } | null>(null);

  useEffect(() => {
    const onResize = debounce(() => {
      if (isModal) {
        const config = getMasonryConf();

        const result = masonryCompute({
          containerWidth:
            document.documentElement.clientWidth - config.margin * 2,
          ...config,
        });

        setColumn({
          num: result.cols,
          columnSize: result.columnSize,
          wrapperWidth:
            result.cols * result.columnSize +
            config.itemGap * (result.cols - 1),
        });

        // 设置列宽
        containerRef.current?.style.setProperty(
          "--column-size",
          `${result.columnSize}px`
        );
      }
    }, 300);

    onResize();

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [isModal]);

  // const { aiGameplayList } = usePinDetailAiGameplay(pin);

  // 自定义PinRecommendPinList组件，插入同在画板运营位
  const customPinRecommendPinList = useMemoizedFn(() => {
    if (!pin?.pin_id || !inViewport) return null;

    return (
      <div className={styles.recommendPinListContainer}>
        {/* {aiGameplayList && <AiGameplay list={aiGameplayList} />} */}

        {/* 大于等于10个画板时，在瀑布流外面渲染Swiper */}
        {boardDataSource.length >= 10 && (
          <BoardSwiper
            boardsData={boardDataSource}
            pinId={pin?.pin_id}
            stackIndex={stackIndex}
            currentPin={pin}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* 为你推荐瀑布流 - 小于10个画板时会作为运营位插入瀑布流第一位 */}
        <PinRecommendPinList
          pinId={pin?.pin_id}
          isModal={isModal}
          fileId={pin?.file_id}
          similiarList={similiarList}
          showSimilarList={showSimilarList}
          customColumn={column?.num}
          customColumnSize={column?.columnSize}
          relatedBoards={boardDataSource}
          currentBoard={pin?.board} // 传递当前采集所在画板
          artRecommend={artRecommend}
          searchWord={searchWord}
        />
      </div>
    );
  });

  return (
    <>
      <div className={styles.list} ref={containerRef}>
        <div
          className={`${styles.wrapper} calculateWrapper`}
          style={column ? { width: column.wrapperWidth } : {}}
        >
          <div
            data-content-source="为你推荐"
            style={{ minHeight: `calc(100vh - ${reduceHeight}px)` }}
          >
            {customPinRecommendPinList()}
          </div>
        </div>
      </div>
    </>
  );
}

export default memo(RecommendNew);
